import { toast } from '../components/Toast';
import { useAuthContext } from '../context/AuthContext';

const sidebarItems = [
    { label: 'My Profile', path: '/account/profile', icon: 'ri-user-line' },
    { label: 'My Addresses', path: '/account/addresses', icon: 'ri-map-pin-line' },
    { label: 'My Orders', path: '/account/orders', icon: 'ri-shopping-bag-line' },
    { label: 'Loyalty Points', path: '/account/loyalty', icon: 'ri-coin-line' },
    { label: 'Wishlist', path: '/account/wishlist', icon: 'ri-heart-line' },
    { label: 'Support Tickets', path: '/account/support', icon: 'ri-customer-service-line' },
    { label: 'Bulk Inquiries', path: '/account/bulk-inquiries', icon: 'ri-file-list-3-line' },
];

export default function AccountLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const { customer, logout } = useAuthContext();

    const handleLogout = async () => {
        try {
            await logout();
            toast.success('Logged out successfully');
            navigate('/login', { replace: true });
        } catch {
            toast.error('Failed to logout. Please try again.');
        }
    };

    return (
        <div className="min-h-screen bg-muted pt-[10px] pb-16">
            <div className="max-w-[1400px] mx-auto px-4">
                {/* Page Title */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-foreground">My Account</h1>
                    <p className="text-muted-foreground text-sm mt-1">Manage your account settings and preferences</p>
                </div>

                <div className="flex flex-col lg:flex-row gap-6">
                    {/* Sidebar — Desktop */}
                    <aside className="hidden lg:block w-72 flex-shrink-0">
                        <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
                            {/* Customer Info */}
                            <div className="p-5 border-b border-border">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                        <i className="ri-user-line text-xl text-primary"></i>
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-semibold text-foreground truncate">
                                            {customer?.firstName} {customer?.lastName}
                                        </p>
                                        <p className="text-xs text-muted-foreground truncate">{customer?.email}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Nav Items */}
                            <nav className="p-2">
                                {sidebarItems.map((item) => {
                                    const isActive = location.pathname === item.path;
                                    return (
                                        <NavLink
                                            key={item.path}
                                            to={item.path}
                                            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                                                isActive
                                                    ? 'bg-primary/10 text-primary'
                                                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                            }`}
                                        >
                                            <i className={`${item.icon} text-lg`}></i>
                                            {item.label}
                                        </NavLink>
                                    );
                                })}

                                {/* Logout */}
                                <button
                                    onClick={handleLogout}
                                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 transition-colors mt-1"
                                >
                                    <i className="ri-logout-box-line text-lg"></i>
                                    Logout
                                </button>
                            </nav>
                        </div>
                    </aside>

                    {/* Mobile Tabs */}
                    <div className="lg:hidden">
                        <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
                            {/* Customer Info — Mobile */}
                            <div className="p-4 border-b border-border flex items-center gap-3">
                                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                    <i className="ri-user-line text-lg text-primary"></i>
                                </div>
                                <div className="min-w-0">
                                    <p className="font-semibold text-foreground text-sm truncate">
                                        {customer?.firstName} {customer?.lastName}
                                    </p>
                                    <p className="text-xs text-muted-foreground truncate">{customer?.email}</p>
                                </div>
                            </div>

                            {/* Scrollable Tabs */}
                            <div className="flex overflow-x-auto scrollbar-hide">
                                {sidebarItems.map((item) => {
                                    const isActive = location.pathname === item.path;
                                    return (
                                        <NavLink
                                            key={item.path}
                                            to={item.path}
                                            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                                                isActive
                                                    ? 'border-primary text-primary'
                                                    : 'border-transparent text-muted-foreground hover:text-foreground'
                                            }`}
                                        >
                                            <i className={`${item.icon} text-base`}></i>
                                            {item.label}
                                        </NavLink>
                                    );
                                })}
                                <button
                                    onClick={handleLogout}
                                    className="flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 border-transparent text-red-500 hover:text-red-600 transition-colors"
                                >
                                    <i className="ri-logout-box-line text-base"></i>
                                    Logout
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Content Area */}
                    <main className="flex-1 min-w-0">
                        <Outlet />
                    </main>
                </div>
            </div>
        </div>
    );
}
